/**
 * `PlansView` — the plan-catalog list.
 *
 * GET /internal/admin/plans returns every plan bundled with its prices,
 * entitlements AND features so the table renders in one round-trip.
 *
 * ADR-0263 S1: catalog grows a `scope` + `catalog_version` axis. The
 * list groups plans into three sections:
 *   1. Per-site plans (2026)  — scope=site, catalog_version=2
 *   2. Account plans          — scope=account, catalog_version=2
 *   3. Legacy plans           — catalog_version=1 (collapsed by default)
 *
 * A tab strip at the top of the view switches to the Add-ons list
 * (same URL route, local state only — the nav is already crowded, so
 * add-ons stay inside Plans per the founder rule).
 *
 * The INERT banner (v2 flavor) sits at the top: catalog is draft;
 * nothing limits a site or charges anyone yet. The existing Stripe
 * sync button is untouched.
 */

import { ChevronDown, ChevronRight, Plus, RefreshCw } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import type { AdminPlanDetail } from '@sill/types/api';

import { useAdminApi } from '../../api/api-context.js';
import { ApiError } from '../../api/http-client.js';
import { useAuth } from '../../app/auth-context.js';
import type { SetView } from '../../app/view-router.js';
import { Card } from '../../components/atoms/Card.js';
import { Pill } from '../../components/atoms/Pill.js';
import { useC } from '../../lib/theme.js';
import { formatIsoCompact } from '../../lib/format.js';
import { hasRole } from '../../lib/roles.js';
import { AddonsTab } from './AddonsTab.js';
import { InertBanner } from './InertBanner.js';
import { PlansTabs, type PlansTabId } from './PlansTabs.js';
import { PricingPagePreviewPanel } from './PricingPagePreviewPanel.js';
import {
  findPrice,
  formatPriceLabel,
  groupPlans,
  planStatusTone,
  scopeLabel,
  summarizeEntitlements,
} from './plan-helpers.js';

const serifFont = "'Instrument Serif', serif";
const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

export interface PlansViewProps {
  setView: SetView;
}

export function PlansView({ setView }: PlansViewProps): JSX.Element {
  const C = useC();
  const api = useAdminApi();
  const auth = useAuth();
  const canCreate = hasRole(
    auth.state.kind === 'authenticated' ? auth.state.staff.role : undefined,
    'admin',
  );

  const [tab, setTab] = useState<PlansTabId>('plans');

  const [plans, setPlans] = useState<readonly AdminPlanDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ADR-0155 D1 — "Sync to Stripe" mirrors the catalog into the billing
  // provider (test-mode until the live gate flips) so paid plans become
  // purchasable at checkout. Idempotent; safe to re-run.
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState<
    { tone: 'ok' | 'err'; text: string } | null
  >(null);

  async function onSync(): Promise<void> {
    setSyncing(true);
    setSyncMsg(null);
    try {
      // v1 catalog (catalog_version=1): the legacy plan set still
      // seeds Stripe prices for grandfathered merchant accounts.
      const v1 = await api.syncBillingProviders();
      // v2 catalog (catalog_version=2): per-site plans + add-ons under
      // ADR-0263. Without this, v2 plans/add-ons never get Stripe
      // prices from the UI (the v1 route filters out v2 rows).
      const v2 = await api.syncBillingProvidersV2();
      const synced
        = v1.synced.length + v2.plans.synced.length + v2.addons.synced.length;
      const skipped
        = v1.skipped.length + v2.plans.skipped.length + v2.addons.skipped.length;
      const failed
        = v1.failed.length + v2.plans.failed.length + v2.addons.failed.length;
      const parts = [`${synced} synced`, `${skipped} already current`];
      if (failed > 0) parts.push(`${failed} failed`);
      setSyncMsg({
        tone: failed > 0 ? 'err' : 'ok',
        text: `Stripe sync complete — ${parts.join(', ')}.`,
      });
    } catch (err) {
      if (err instanceof ApiError && err.kind === 'unauthorized') return;
      const reason =
        err instanceof ApiError && err.server_reason !== undefined
          ? ` (${err.server_reason})`
          : '';
      setSyncMsg({ tone: 'err', text: `Sync failed${reason}.` });
    } finally {
      setSyncing(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async (): Promise<void> => {
      try {
        const res = await api.getPlans();
        if (!cancelled) setPlans(res.plans);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.kind === 'unauthorized') return;
        const reason =
          err instanceof ApiError && err.server_reason !== undefined
            ? ` (${err.server_reason})`
            : '';
        setError(`Could not load plans${reason}.`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api]);

  const grouped = useMemo(() => groupPlans(plans), [plans]);

  return (
    <div className="max-w-6xl mx-auto px-3 sm:px-8 py-6 sm:py-10">
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          color: C.muted,
          letterSpacing: 2,
          textTransform: 'uppercase',
          marginBottom: 10,
        }}
      >
        Plans
      </div>
      <div className="flex flex-wrap items-end justify-between gap-3 sm:gap-4 mb-5 sm:mb-6">
        <div className="flex items-baseline gap-3 flex-wrap">
          <h1
            style={{
              fontFamily: serifFont,
              fontSize: 'clamp(28px, 6vw, 44px)',
              lineHeight: 1,
              color: C.text,
              letterSpacing: -1.5,
              margin: 0,
            }}
          >
            Plan catalog
          </h1>
          <span
            style={{
              fontFamily: monoFont,
              fontSize: 14,
              color: C.muted,
              letterSpacing: 0.5,
            }}
            aria-label={`${plans.length} plans in the catalog`}
          >
            ({plans.length})
          </span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => void onSync()}
            disabled={syncing}
            className="flex items-center gap-2"
            style={{
              background: 'transparent',
              color: C.text,
              fontFamily: sansFont,
              fontSize: 13,
              fontWeight: 500,
              padding: '10px 14px',
              borderRadius: 2,
              border: `1px solid ${C.border}`,
              minHeight: 44,
              cursor: syncing ? 'not-allowed' : 'pointer',
              opacity: syncing ? 0.6 : 1,
            }}
            title="Create the Stripe product/price for every paid plan so it becomes purchasable. Idempotent."
          >
            <RefreshCw size={14} aria-hidden="true" />
            {syncing ? 'Syncing…' : 'Sync to Stripe'}
          </button>
          {canCreate && tab === 'plans' ? (
            <button
              type="button"
              onClick={() => setView('plan-new')}
              className="flex items-center gap-2"
              style={{
                background: C.lime,
                color: C.ink,
                fontFamily: sansFont,
                fontSize: 13,
                fontWeight: 600,
                padding: '10px 14px',
                borderRadius: 2,
                border: 0,
                minHeight: 44,
              }}
            >
              <Plus size={14} aria-hidden="true" />
              New plan
            </button>
          ) : null}
          {canCreate && tab === 'addons' ? (
            <button
              type="button"
              onClick={() => setView('addon-new')}
              className="flex items-center gap-2"
              style={{
                background: C.lime,
                color: C.ink,
                fontFamily: sansFont,
                fontSize: 13,
                fontWeight: 600,
                padding: '10px 14px',
                borderRadius: 2,
                border: 0,
                minHeight: 44,
              }}
            >
              <Plus size={14} aria-hidden="true" />
              New add-on
            </button>
          ) : null}
        </div>
      </div>

      <PlansTabs value={tab} onChange={setTab} />

      <InertBanner flavor="v2" />

      {syncMsg !== null ? (
        <Card style={{ padding: 16, marginBottom: 12 }}>
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              color: syncMsg.tone === 'ok' ? C.limeFg : C.coralFg,
              letterSpacing: 2,
              textTransform: 'uppercase',
              marginBottom: 6,
            }}
          >
            {syncMsg.tone === 'ok' ? 'Stripe sync' : 'Sync error'}
          </div>
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.text }}>
            {syncMsg.text}
          </div>
        </Card>
      ) : null}

      {tab === 'plans' ? (
        <PlansTabPanel
          loading={loading}
          error={error}
          grouped={grouped}
          setView={setView}
        />
      ) : tab === 'addons' ? (
        <AddonsTab setView={setView} />
      ) : (
        <PricingPagePreviewPanel />
      )}
    </div>
  );
}

function PlansTabPanel({
  loading,
  error,
  grouped,
  setView,
}: {
  loading: boolean;
  error: string | null;
  grouped: ReturnType<typeof groupPlans>;
  setView: SetView;
}): JSX.Element {
  const C = useC();
  const [legacyOpen, setLegacyOpen] = useState(false);
  const totalCount =
    grouped.siteV2.length + grouped.accountV2.length + grouped.legacy.length;

  if (error !== null) {
    return (
      <Card style={{ padding: 16 }}>
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.coralFg,
            letterSpacing: 2,
            textTransform: 'uppercase',
            marginBottom: 6,
          }}
        >
          Load failed
        </div>
        <div style={{ fontFamily: sansFont, fontSize: 13, color: C.text }}>
          {error}
        </div>
      </Card>
    );
  }

  if (loading && totalCount === 0) {
    return (
      <Card style={{ padding: 24 }}>
        <div
          className="text-center"
          style={{
            fontFamily: monoFont,
            fontSize: 11,
            color: C.muted,
            letterSpacing: 1.5,
          }}
        >
          Loading…
        </div>
      </Card>
    );
  }

  if (totalCount === 0) {
    return (
      <Card style={{ padding: 24 }}>
        <div
          className="text-center"
          style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}
        >
          No plans yet. Click <strong>New plan</strong> to draft one.
        </div>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PlanSection
        title="Per-site plans (2026)"
        subtitle="Free, Protect, Business — one site each."
        plans={grouped.siteV2}
        setView={setView}
      />
      <PlanSection
        title="Account plans"
        subtitle="Standard and agency — one plan per account."
        plans={grouped.accountV2}
        setView={setView}
      />
      <PlanSectionCollapsible
        title="Legacy plans"
        subtitle="Catalog version 1. Grandfathered + staff-assigned accounts."
        plans={grouped.legacy}
        open={legacyOpen}
        onToggle={() => setLegacyOpen((x) => !x)}
        setView={setView}
      />
    </div>
  );
}

function PlanSection({
  title,
  subtitle,
  plans,
  setView,
}: {
  title: string;
  subtitle: string;
  plans: readonly AdminPlanDetail[];
  setView: SetView;
}): JSX.Element {
  return (
    <section aria-label={title}>
      <SectionHeader title={title} subtitle={subtitle} count={plans.length} />
      <PlanList plans={plans} setView={setView} />
    </section>
  );
}

function PlanSectionCollapsible({
  title,
  subtitle,
  plans,
  open,
  onToggle,
  setView,
}: {
  title: string;
  subtitle: string;
  plans: readonly AdminPlanDetail[];
  open: boolean;
  onToggle: () => void;
  setView: SetView;
}): JSX.Element {
  const C = useC();
  return (
    <section aria-label={title}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex items-center gap-2 w-full"
        style={{
          background: 'transparent',
          border: 0,
          padding: 0,
          marginBottom: 10,
          cursor: 'pointer',
          minHeight: 32,
        }}
      >
        {open ? (
          <ChevronDown size={14} color={C.muted} aria-hidden="true" />
        ) : (
          <ChevronRight size={14} color={C.muted} aria-hidden="true" />
        )}
        <div className="flex items-baseline gap-2 flex-wrap">
          <span
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              letterSpacing: 2,
              textTransform: 'uppercase',
              color: C.muted,
            }}
          >
            {title}
          </span>
          <span
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              color: C.dim,
              letterSpacing: 1,
            }}
          >
            ({plans.length}) {subtitle}
          </span>
        </div>
      </button>
      {open ? <PlanList plans={plans} setView={setView} /> : null}
    </section>
  );
}

function SectionHeader({
  title,
  subtitle,
  count,
}: {
  title: string;
  subtitle: string;
  count: number;
}): JSX.Element {
  const C = useC();
  return (
    <div className="flex items-baseline gap-2 flex-wrap" style={{ marginBottom: 10 }}>
      <span
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          letterSpacing: 2,
          textTransform: 'uppercase',
          color: C.muted,
        }}
      >
        {title}
      </span>
      <span
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          color: C.dim,
          letterSpacing: 1,
        }}
      >
        ({count}) {subtitle}
      </span>
    </div>
  );
}

function PlanList({
  plans,
  setView,
}: {
  plans: readonly AdminPlanDetail[];
  setView: SetView;
}): JSX.Element {
  const C = useC();
  if (plans.length === 0) {
    return (
      <Card style={{ padding: 16 }}>
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 12,
            color: C.muted,
          }}
        >
          No plans in this group.
        </div>
      </Card>
    );
  }
  return (
    <>
      {/* Desktop / tablet — table */}
      <Card className="hidden sm:block" style={{ overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table
            style={{
              borderCollapse: 'collapse',
              fontFamily: sansFont,
              width: '100%',
              minWidth: 880,
            }}
          >
            <thead>
              <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                <Th>Name</Th>
                <Th>Status</Th>
                <Th>Scope</Th>
                <Th>Monthly</Th>
                <Th>Yearly</Th>
                <Th>Limits</Th>
                <Th>Updated</Th>
              </tr>
            </thead>
            <tbody>
              {plans.map((row, idx) => (
                <PlanTableRow
                  key={row.plan.plan_id}
                  detail={row}
                  isLast={idx === plans.length - 1}
                  onClick={(): void =>
                    setView('plan-detail', { planId: row.plan.plan_id })
                  }
                />
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Mobile — stacked cards */}
      <div className="sm:hidden grid grid-cols-1 gap-2">
        {plans.map((row) => (
          <PlanMobileCard
            key={row.plan.plan_id}
            detail={row}
            onClick={(): void =>
              setView('plan-detail', { planId: row.plan.plan_id })
            }
          />
        ))}
      </div>
    </>
  );
}

function Th({ children }: { children: React.ReactNode }): JSX.Element {
  const C = useC();
  return (
    <th
      className="text-left px-4 py-3"
      style={{
        fontFamily: monoFont,
        fontSize: 9,
        color: C.muted,
        letterSpacing: 2,
        textTransform: 'uppercase',
        fontWeight: 500,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </th>
  );
}

function PlanTableRow({
  detail,
  isLast,
  onClick,
}: {
  detail: AdminPlanDetail;
  isLast: boolean;
  onClick: () => void;
}): JSX.Element {
  const C = useC();
  const monthly = findPrice(detail.prices, 'monthly');
  const yearly = findPrice(detail.prices, 'yearly');
  return (
    <tr
      onClick={onClick}
      className="cursor-pointer"
      style={{
        borderBottom: isLast ? 'none' : `1px solid ${C.borderSoft}`,
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = C.surface2)}
      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
    >
      <td className="px-4 py-3">
        <div className="flex items-center gap-2 flex-wrap">
          <div
            style={{
              fontFamily: sansFont,
              fontSize: 13,
              color: C.text,
              fontWeight: 500,
            }}
          >
            {detail.plan.name}
          </div>
          {detail.plan.is_default ? <Pill tone="lime">Default</Pill> : null}
          {detail.plan.is_custom ? <Pill tone="amber">Custom</Pill> : null}
        </div>
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.dim,
            marginTop: 2,
          }}
        >
          {detail.plan.slug}
        </div>
      </td>
      <td className="px-4 py-3">
        <Pill tone={planStatusTone(detail.plan.status)}>
          {detail.plan.status}
        </Pill>
      </td>
      <td
        className="px-4 py-3"
        style={{
          fontFamily: monoFont,
          fontSize: 11,
          color: C.body,
          whiteSpace: 'nowrap',
        }}
      >
        {scopeLabel(detail.plan.scope)}
      </td>
      <td
        className="px-4 py-3"
        style={{
          fontFamily: monoFont,
          fontSize: 11,
          color: monthly === undefined ? C.dim : C.body,
          whiteSpace: 'nowrap',
        }}
      >
        {formatPriceLabel(monthly)}
      </td>
      <td
        className="px-4 py-3"
        style={{
          fontFamily: monoFont,
          fontSize: 11,
          color: yearly === undefined ? C.dim : C.body,
          whiteSpace: 'nowrap',
        }}
      >
        {formatPriceLabel(yearly)}
      </td>
      <td
        className="px-4 py-3"
        style={{
          fontFamily: monoFont,
          fontSize: 11,
          color: C.body,
          lineHeight: 1.5,
        }}
      >
        {summarizeEntitlements(detail.entitlements)}
      </td>
      <td
        className="px-4 py-3"
        style={{
          fontFamily: monoFont,
          fontSize: 11,
          color: C.muted,
          whiteSpace: 'nowrap',
        }}
      >
        {formatIsoCompact(detail.plan.updated_at)}
      </td>
    </tr>
  );
}

function PlanMobileCard({
  detail,
  onClick,
}: {
  detail: AdminPlanDetail;
  onClick: () => void;
}): JSX.Element {
  const C = useC();
  const monthly = findPrice(detail.prices, 'monthly');
  const yearly = findPrice(detail.prices, 'yearly');
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left w-full"
      style={{
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 3,
        padding: 14,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        minHeight: 44,
      }}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div
              style={{
                fontFamily: sansFont,
                fontSize: 14,
                color: C.text,
                fontWeight: 500,
                lineHeight: 1.3,
                wordBreak: 'break-word',
              }}
            >
              {detail.plan.name}
            </div>
            {detail.plan.is_default ? <Pill tone="lime">Default</Pill> : null}
          </div>
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 11,
              color: C.muted,
              marginTop: 2,
            }}
          >
            {detail.plan.slug} · {scopeLabel(detail.plan.scope)}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          <Pill tone={planStatusTone(detail.plan.status)}>
            {detail.plan.status}
          </Pill>
          {detail.plan.is_custom ? <Pill tone="amber">Custom</Pill> : null}
        </div>
      </div>
      <div
        className="flex items-center justify-between gap-3 flex-wrap"
        style={{
          fontFamily: monoFont,
          fontSize: 11,
          color: C.body,
        }}
      >
        <span>{formatPriceLabel(monthly)}</span>
        <span style={{ color: C.dim }}>·</span>
        <span>{formatPriceLabel(yearly)}</span>
      </div>
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 11,
          color: C.body,
          lineHeight: 1.5,
          wordBreak: 'break-word',
        }}
      >
        {summarizeEntitlements(detail.entitlements)}
      </div>
    </button>
  );
}
